import { inject, Injectable } from '@angular/core';
import { UploadGuard } from '@app/guards/upload-guard';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { ActionDisabledCondition, ActionItemOptions, SearchService, UserAction } from '@mediahaven/shared';
import {
    ApiFragment,
    ApiQuery,
    Combine,
    defaultPaginationInfo,
    defaultSortInfo,
    Mh2Entity,
    NavigationLink,
    QueryPart,
    QueryPartStrategy,
    RecordRestrictions,
    RecordTypeStructure,
    Restriction,
    VersionManagementGuard,
    ZoneRestrictions
} from '@stylehaven/components';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UploadHelper } from '../../../../upload/upload-helper.sandbox';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class AddNewOriginalActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.ADD_NEW_ORIGINAL;

    // Services
    private readonly searchService: SearchService = inject(SearchService);
    private readonly versionManagementGuard: VersionManagementGuard = inject(VersionManagementGuard);
    private readonly uploadGuard: UploadGuard = inject(UploadGuard);
    private readonly uploadHelper: UploadHelper = inject(UploadHelper);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /*  Add New Original
     *  Available when:
     *   * User can upload
     *   * It is not Processing
     *   * It is not deleted
     *   * It is not a representation
     *
     *  Disabled when:
     *   * It has a representation in Draft
     *   * It is not editable
     *   * It is processing
     *   * It has a pending task
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        const hasDraftRepresentations$ = this.hasDraftRepresentations(record);
        const versionManagementActive$ = this.versionManagementGuard.hasAccess();
        const canUpload$ = this.uploadGuard.hasAccess();

        return combineLatest([hasDraftRepresentations$, versionManagementActive$, canUpload$]).pipe(
            map(([hasDraftRepresentations, versionManagementActive, canUpload]) => {
                return this.userActionBuilder(record, hasDraftRepresentations, versionManagementActive, canUpload);
            })
        );
    }

    public userActionBuilder(
        record: ApiFragment,
        hasDraftRepresentations: boolean,
        versionManagementActive: boolean,
        canUpload: boolean
    ): UserAction {
        const isRepresentation: boolean = record.getRecordStructure() === RecordTypeStructure.Representation;

        const isDeleted: boolean = record.isDeleted();
        const isPending: boolean = record.hasPendingTask();
        const isEditable: boolean = !record.isProcessing() && record.hasEditableStatus() && record.hasEditRights();

        const availableConditions: boolean[] = [canUpload, versionManagementActive, !isDeleted, !isRepresentation];
        const disableConditions: ActionDisabledCondition[] = [
            { condition: hasDraftRepresentations, tooltip: 'representations.has-draft' },
            { condition: !isEditable, tooltip: 'general.blocked-status' },
            { condition: isPending, tooltip: 'general.pending-task' }
        ];

        return new UserAction(availableConditions, disableConditions);
    }

    private hasDraftRepresentations(record: ApiFragment): Observable<boolean> {
        const representationsRestriction: Restriction = Combine.And(
            RecordRestrictions.representationsOfRecord(record.getRecordId()),
            ZoneRestrictions.fullZoneSearch()
        );

        const draftStatus: QueryPart = new QueryPart({
            field: 'Structural.Versioning.Status',
            strategy: QueryPartStrategy.MatchAll,
            values: ['Draft']
        });

        const query: ApiQuery = new ApiQuery([draftStatus], representationsRestriction);
        return this.searchService
            .getRecords(defaultPaginationInfo, defaultSortInfo, query)
            .pipe(map(paginatedResults => paginatedResults.paginationInfo.totalNumberOfResults > 0));
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const selectMultipleFiles: boolean = false;
        const files$: Observable<FileList> = this.uploadHelper.askUserForFiles(selectMultipleFiles);

        return files$.pipe(
            map(files => files.item(0)),
            switchMap(file => (file ? this.uploadHelper.uploadNewOriginal(file, record.getFragmentId()) : of(false)))
        );
    }

    public override getLabelKey(target: ApiFragment): string {
        return 'actions.add-new-original';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.add-new-original';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return Mh2Entity.Generic;
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return 'action-toasts.add-new-original.success-link';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        if (!actionItemOptions.contextFragment) {
            console.error('No context fragment found for action item options');
        }
        return actionItemOptions.contextFragment
            ? {
                  route: `${actionItemOptions.contextFragment.getDetailUrl()}/representations`
              }
            : undefined;
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
